import type { Currency } from '@seaber/common/graphql/types';
import { type ScenarioLocation } from '../../../lib/planningData';
import type { CurrencyRates } from '../../../state/zustand';
import type { Vessel } from '../../core/Vessel';
import { stringToNumOpt } from '../../core/util';

type PnlProcessorVessel = {
  grossTonnage: number | null;
  id: number;
  name: string;
  shortName: string | null;
};

/** Context object shared between all pnl processors */
export interface PnlProcessorContext {
  currency: {
    defaultCurrency: Currency;
    rates: CurrencyRates;
  };
  locationById: Map<number, ScenarioLocation>;
  /** Registered processors */
  processors: Set<string>;
  /** A processor can pass data to any processors following it via the shared context */
  shared: Record<string, unknown>;
  /** The timeline type `vessel` for TC/Vessel timelines and `contract` for COA/Spot/Relet */
  type: 'vessel' | 'contract';
  /** Associated vessel, can be null when type is contract */
  vessel: PnlProcessorVessel | null;
}
export interface PnlProcessorContextInput {
  currency: {
    defaultCurrency: Currency;
    rates: CurrencyRates;
  };
  locationById: Map<number, ScenarioLocation>;
  type: 'vessel' | 'contract';
  vessel: PnlProcessorVessel | null;
}

export const getPnlProcessorVessel = (
  vessel: Vessel,
): PnlProcessorContext['vessel'] => {
  return {
    grossTonnage: stringToNumOpt(vessel.grossTonnage) ?? null,
    id: vessel.id,
    name: vessel.name ?? 'N/A',
    shortName: vessel.shortName ?? null,
  };
};

/** Initializes pnl processor context */
export function createPnlProcessorContext(
  input: PnlProcessorContextInput,
): PnlProcessorContext {
  return {
    currency: input.currency,
    locationById: input.locationById,
    processors: new Set(),
    shared: {},
    type: input.type,
    vessel: input.vessel,
  };
}

export function registerPnlProcessor(
  context: PnlProcessorContext,
  processorId: string,
  dependencies: string[],
): void {
  // Check that dependencies have been registered before adding the processor
  for (const dependency of dependencies) {
    if (!context.processors.has(dependency)) {
      throw new Error(
        `Processor "${processorId}" depends on "${dependency}" which is not registered.`,
      );
    }
  }

  // Check that processor has not already been registered
  if (context.processors.has(processorId)) {
    throw new Error(`Processor "${processorId}" has already been registered.`);
  }

  context.processors.add(processorId);
}

export type DependencyAccessor<T> = (context: PnlProcessorContext) => T;

/** Creates a typed accessor for a given property in the shared context */
export function createDependencyAccessor<T>(
  key: string,
): DependencyAccessor<T> {
  return (context: PnlProcessorContext): T => {
    const value = context.shared[key];
    if (value === undefined) {
      throw new Error(`Required dependency data "${key}" not found in context`);
    }
    return value as T;
  };
}
